import React, {
  type FormEvent,
  useState,
  forwardRef,
  useContext,
  useEffect,
} from 'react';
import { TextField } from 'braid-design-system';
import { TrackingContext } from './../../context/trackingContext';
import {
  isTextValid,
  isTextMissing,
  isTextTooShort,
  isTextTooLong,
} from './validation';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';

export interface JobTitleChangeEvent {
  isValid: boolean;
  value: string;
}
export type JobTitleChangeHandler = (event: JobTitleChangeEvent) => void;

interface JobTitleProps {
  id: string;
  value?: string;
  label?: string;
  onChange?: JobTitleChangeHandler;
  isValidationRequested?: boolean;
  maxLength?: number;
  minLength?: number;
}

export const JobTitle = forwardRef<HTMLInputElement, JobTitleProps>(
  (
    {
      id,
      value,
      onChange,
      isValidationRequested,
      label = 'Job title',
      maxLength = 80,
      minLength = 3,
      ...restProps
    },
    ref,
  ) => {
    const [text, setText] = useState(value ?? '');
    const [isValid, setIsValid] = useState(true);
    const tracking = useContext(TrackingContext);
    const { t } = useTranslations(translations);

    useEffect(() => {
      if (onChange) {
        onChange({
          isValid,
          value: text,
        });
      }
    }, [text]);

    const validate = (textValue: string) => {
      const valid = isTextValid(textValue, minLength, maxLength);
      setIsValid(valid);
    };

    const onChangeInternal = (event: FormEvent<HTMLInputElement>) => {
      const textValue = event.currentTarget.value;

      if (isValidationRequested) {
        validate(textValue);
      }

      setText(textValue);
    };

    const getValidationMessage = (
      text: string,
      minLength: number,
      maxLength: number,
    ) => {
      if (isTextMissing(text)) {
        return t('Please enter a job title');
      }

      if (isTextTooShort(text, minLength)) {
        return t('your job title needs to be a minimum...', {
          minLength: minLength.toString(),
        });
      }

      if (isTextTooLong(text, maxLength)) {
        return t('your job title needs to be a maximum...', {
          maxLength: maxLength.toString(),
        });
      }

      return '';
    };

    return (
      <TextField
        id={id}
        value={text}
        label={label}
        type="text"
        onChange={onChangeInternal}
        onBlur={() => tracking.onRoleTitleUpdated()}
        tone={isValid ? 'neutral' : 'critical'}
        message={getValidationMessage(text, minLength, maxLength)}
        ref={ref}
        {...restProps}
      />
    );
  },
);
